import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import Alert from "@material-ui/lab/Alert";
import Skeleton from "@material-ui/lab/Skeleton";
import { useHistory, useParams } from "react-router-dom";

import { repliValidator } from "../../../components";

import useStyles from "./Update.style";
import Cookies from "universal-cookie";
import { department } from "../../../data/db";
import { ProviderSelect } from "../../mobile/profiler/InputComponents";

const cookies = new Cookies();

const UpdateUserTextField = (props) => {
  return <TextField variant="outlined" margin="normal" fullWidth {...props} />;
};

const Update = () => {
  let history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation();
  const { userId } = useParams();
  const [email, setEmail] = useState("");
  const [availableEmail, setAvailableEmail] = useState(true);
  const [emailUnavailbilityMessage, setEmailUnavailbilityMessage] =
    useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [departmentValue, setDepartmentValue] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [organizationList, setOrganizationList] = useState([]);
  const [organizationsData, setOrganizationsData] = useState([]);
  const [jobTitle, setJobTitle] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [saveResult, setSaveResult] = useState();
  const [loginUserId, setLoginUserId] = useState("");

  const MenuProps = {
    getContentAnchorEl: null,
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "center",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "center",
    },
    variant: "menu",
  };

  const validator = useRef(
    repliValidator({
      validators: {
        password_confirmation: {
          message: t("common.message.validation.passwordConfirmation"),
          rule: (val, params, validator) => {
            return val === params[0];
          },
          required: true,
        },
      },
    })
  );



  useEffect(() => {
    if (redirect) {
      setTimeout(() => {
        return history.push("/provider/user");
      }, 2000);
    }
  }, [redirect, history]);

  useEffect(() => {
    const getUser = async () => {
      try {
        let response = await fetch(
          `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_USER}/${userId}`,
          {
            headers: {
              "X-ACCESS-TOKEN": cookies.get("token"),
            },
          }
        );
        let json = await response.json();
        if (!response.ok) {
          setError(json.message || json.errors[0].msg);
          return;
        }

        setOrganizations(json.organizations);
        setEmail(json.email);
        setFirstName(json.firstName);
        setLastName(json.lastName);
        setRoles(json.roles);
        setJobTitle(json.jobTitle === "undefined" ? "" : json.jobTitle);
        setDepartmentValue(json.department);
      } catch (error) {
        console.log(error);
        setError(
          `${t("module.provider.editUser.message.fetchDataFailure")}. ${t(
            "common.message.error.seeLog"
          )}.`
        );
      } finally {
        setLoading(false);
      }
    };

    getUser();
    validator.current.showMessages();
  }, [userId, t]);

  useEffect(() => {
    // getting organization details
    const getOrganizations = async () => {
      try {
        let response = await fetch(
          `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_ORGANIZATION}`,
          {
            headers: {
              "X-ACCESS-TOKEN": cookies.get("token"),
            },
          }
        );

        let json = await response.json();
        if (!response.ok) {
          console.log(json.message);
          return;
        }

        setOrganizationsData(json);

        // getting user details
        await fetch(
          `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_AUTH}/app/me`,
          {
            headers: {
              "X-ACCESS-TOKEN": cookies.get("token"),
            },
          }
        )
          .then((res) => res.json())
          .then((res) => {
            setLoginUserId(res._id);
          });
      } catch (error) {
        console.log(error);
        console.log(
          `${t(
            "module.provider.listOrganizations.message.fetchDataFailure"
          )}. ${t("common.message.error.seeLog")}.`
        );
      }
    };

    getOrganizations();
  }, [t]);

  useEffect(() => {
    if (organizationsData && organizationsData.length > 0) {
      setOrganizationList(organizationsData.map((org) => org.name));
    }
  }, [organizationsData]);

  useEffect(() => {
    setEmailUnavailbilityMessage(
      availableEmail
        ? ""
        : t("module.provider.addPatient.message.duplicatedEmail")
    );
  }, [availableEmail, t]);

  const checkDuplicatedEmail = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_PATIENT_GETBYEMAIL
        }?${new URLSearchParams({
          email: email,
          id: userId,
        })}`,
        {
          headers: {
            "X-ACCESS-TOKEN": cookies.get("token"),
          },
        }
      );
      setAvailableEmail(response.ok);
      validator.showMessageFor("email");
    } catch (error) {
      console.log(error);
    }
  };

  const getPutParameters = () => {
    let params = new URLSearchParams({
      id: userId,
      email: email,
      firstName: firstName,
      lastName: lastName,
      roles: roles,
      jobTitle: jobTitle,
      department: departmentValue,
      organizations: organizations,
      updatedBy: loginUserId
    });
    if (password) {
      params.append("password", password);
      params.append("passwordConfirmation", passwordConfirmation);
    }

    return params;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validator.current.allValid();
    if (!isValid) {
      validator.current.showMessages();
      return;
    }

    setSending(true);
    try {
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_USER}`,
        {
          method: "put",
          headers: {
            "X-ACCESS-TOKEN": cookies.get("token"),
          },
          body: getPutParameters(),
        }
      );
      if (!response.ok) {
        let json = await response.json();
        let message = " ";
        if (json.errors.length > 1) {
          json.errors.forEach((err) => {
            message += " " + err.msg;
          });
          setSaveResult({ severity: "error", message: message });
        } else {
          setSaveResult({ severity: "error", message: json.errors[0].msg });
        }
        return;
      }
      setRedirect(true);
      setSaveResult({
        severity: "success",
        message: t("module.provider.editUser.message.editUserSuccess"),
      });
    } catch (error) {
      console.log(error);
      setSaveResult({
        severity: "error",
        message: `${t("module.provider.editUser.message.editUserFailure")}.${t(
          "common.message.error.seeLog"
        )}.`,
      });
    } finally {
      setSending(false);
    }
  };

  const handleRoleCheck = (e) => {
    setOrganizations([])
    if (e.target.checked) setRoles([...roles, e.target.name]);
    else setRoles(roles.filter((item) => item !== e.target.name));
  };

  const isRoleChecked = (role) => {
    return roles && roles.includes(role);
  };

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <div>
      <Typography variant="h4" className={classes.title}>
        {t("module.provider.editUser.title")}
      </Typography>
      {saveResult && (
        <Alert severity={saveResult.severity}>{saveResult.message}</Alert>
      )}
      <form className={classes.form} onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {loading ? (
              <Skeleton variant="rectangle" />
            ) : (
              <UpdateUserTextField
                label={`${t("common.form.label.email")}*`}
                // InputProps={{ readOnly: true }}
                error={
                  !validator.current.fieldValid("email") || !availableEmail
                }
                helperText={
                  validator.current.message("email", email, "required|email") ||
                  emailUnavailbilityMessage
                }
                onChange={(e) => setEmail(e.target.value)}
                onBlur={checkDuplicatedEmail}
                value={email}
              />
            )}
          </Grid>
          <Grid item xs={6}>
            {loading ? (
              <Skeleton variant="rectangle" />
            ) : (
              <UpdateUserTextField
                label={`${t("common.form.label.firstName")}*`}
                autoComplete="firstname"
                error={!validator.current.fieldValid("firstName")}
                helperText={validator.current.message(
                  "firstName",
                  firstName,
                  "required"
                )}
                onChange={(e) => setFirstName(e.target.value)}
                value={firstName}
              />
            )}
          </Grid>
          <Grid item xs={6}>
            {loading ? (
              <Skeleton variant="rectangle" />
            ) : (
              <UpdateUserTextField
                label={`${t("common.form.label.lastName")}*`}
                autoComplete="lastname"
                error={!validator.current.fieldValid("lastName")}
                helperText={validator.current.message(
                  "lastName",
                  lastName,
                  "required|alpha"
                )}
                onChange={(e) => setLastName(e.target.value)}
                value={lastName}
              />
            )}
          </Grid>
          <Grid item xs={6}>
            {loading ? (
              <Skeleton variant="rectangle" />
            ) : (
              <UpdateUserTextField
                label={t("common.form.label.password")}
                autoComplete="password"
                type="password"
                error={!validator.current.fieldValid("password")}
                helperText={
                  validator.current.message("password", password, `min:8`) ||
                  t("module.provider.editUser.message.passwordHelpText")
                }
                onChange={(e) => setPassword(e.target.value.trim())}
                value={password}
              />
            )}
          </Grid>
          <Grid item xs={6}>
            {loading ? (
              <Skeleton variant="rectangle" />
            ) : (
              <UpdateUserTextField
                label={t("common.form.label.passwordConfirmation")}
                autoComplete="passwordconfirmation"
                type="password"
                error={!validator.current.fieldValid("passwordConfirmation")}
                helperText={validator.current.message(
                  "passwordConfirmation",
                  passwordConfirmation,
                  `password_confirmation:${password}`
                )}
                onChange={(e) => setPasswordConfirmation(e.target.value.trim())}
                value={passwordConfirmation}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {loading ? (
              <Skeleton variant="rectangle" />
            ) : (
              <FormControl
                component="fieldset"
                error={!validator.current.fieldValid("roles")}
              >
                <FormLabel
                  component="legend"
                  className={classes.formLabelStyle}
                >
                  {`${t("common.form.label.roles")}*`}
                </FormLabel>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isRoleChecked("patient")}
                        onChange={handleRoleCheck}
                        name="patient"
                      />
                    }
                    label="Patient"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isRoleChecked("provider")}
                        onChange={handleRoleCheck}
                        name="provider"
                      />
                    }
                    label="Provider"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isRoleChecked("administrator")}
                        onChange={handleRoleCheck}
                        name="administrator"
                      />
                    }
                    label="Administrator"
                  />
                </FormGroup>
                <FormHelperText>
                  {validator.current.message("roles", roles, `required`) ||
                    t("module.provider.editUser.message.rolesHelpText")}
                </FormHelperText>
              </FormControl>
            )}
          </Grid>

          {roles.length < 1 ? null : (
            <Grid container spacing={2}>
              {roles.length === 1 && roles[0] === "patient" ? (
                <Grid container spacing={2} style={{padding:'0% 3%', marginBottom:'2%'}}>
                  <Grid item xs={6}>
                    <FormControl
                      id="organization"
                      fullWidth
                      variant="outlined"
                      style={{ margin: "16px 0 8px 0" }}
                    >
                      <InputLabel>Organization</InputLabel>
                      <Select
                        label="Organization"
                        multiple
                        value={organizations}
                        onChange={(e) => {
                          setSaveResult();
                          if (e.target.value.length > 1) {
                            setSaveResult({
                              severity: "error",
                              message: "Maximum one organization only for patients.",
                            });
                          } else {
                            setOrganizations(e.target.value);
                          }
                        }}
                        inputProps={{
                          name: "organization",
                          id: "organization",
                        }}
                        renderValue={(selected) => selected.join(", ")}
                        MenuProps={MenuProps}
                      >
                        {organizationList.map((option, i) => (
                          <MenuItem key={i} value={option}>
                            <ListItemIcon>
                              <Checkbox
                                color="primary"
                                checked={organizations.indexOf(option) > -1}
                              />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <ProviderSelect
                      id="department"
                      label={`${t("common.form.label.department")}`}
                      value={departmentValue}
                      items={[...department]}
                      fullWidth
                      margin="normal"
                      onChange={(e) => setDepartmentValue(e.target.value)}
                    />
                  </Grid>
                </Grid>

              ) : (
                <>
                  <Grid item xs={6}>
                    <FormControl
                      id="organization"
                      fullWidth
                      variant="outlined"
                      style={{ margin: "16px 0 8px 0" }}
                    >
                      <InputLabel>Organization</InputLabel>
                      <Select
                        label="Organization"
                        multiple
                        value={organizations}
                        onChange={(e) => {
                          setSaveResult();
                          if (e.target.value.length > 3) {
                            setSaveResult({
                              severity: "error",
                              message: "Maximum three Organizations only.",
                            });
                          } else {
                            setOrganizations(e.target.value);
                          }
                        }}
                        inputProps={{
                          name: "organization",
                          id: "organization",
                        }}
                        renderValue={(selected) => selected.join(", ")}
                        MenuProps={MenuProps}
                      >
                        {organizationList.map((option, i) => (
                          <MenuItem key={i} value={option}>
                            <ListItemIcon>
                              <Checkbox
                                color="primary"
                                checked={organizations.indexOf(option) > -1}
                              />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <UpdateUserTextField
                      label={`${t("common.form.label.jobTitle")}`}
                      autoComplete="off"
                      onChange={(e) => setJobTitle(e.target.value.trim())}
                      value={jobTitle}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ProviderSelect
                      id="department"
                      label={`${t("common.form.label.department")}`}
                      value={departmentValue}
                      items={[...department]}
                      fullWidth
                      margin="normal"
                      onChange={(e) => setDepartmentValue(e.target.value)}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </Grid>
        <Button
          className={classes.button}
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={sending}
        >
          {t("common.form.button.save")}
          {sending && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </Button>
      </form>
    </div>
  );
};

export default Update;
